import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import * as rivalryApi from '../api/rivalry';
import supabase from '../supabaseClient';

const RivalryContext = createContext(null);

export function RivalryProvider({ children }) {
  // State for rivalries
  const [rivalries, setRivalries] = useState([]);
  const [activeRivalry, setActiveRivalry] = useState(null);
  const [loading, setLoading] = useState(false);

  // State for current rivalry
  const [leaderboard, setLeaderboard] = useState([]);
  const [messages, setMessages] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [currentSeason, setCurrentSeason] = useState(null);

  // State for fighter profile
  const [fighterProfile, setFighterProfile] = useState(null);

  // State for user progress
  const [badges, setBadges] = useState([]);
  const [titles, setTitles] = useState([]);
  const [credits, setCredits] = useState(null);

  // Real-time subscriptions
  const [subscriptions, setSubscriptions] = useState([]);

  // ============================================================================
  // LOAD USER DATA
  // ============================================================================

  /**
   * Load all rivalries for the current user
   */
  const loadUserRivalries = async () => {
    setLoading(true);
    const { data, error } = await rivalryApi.getUserRivalries();
    if (!error && data) {
      setRivalries(data);
    } else if (error) {
      console.error('Error loading rivalries:', error);
    }
    setLoading(false);
  };

  /**
   * Load fighter profile for current user
   */
  const loadFighterProfile = async () => {
    const { data, error } = await rivalryApi.getFighterProfile();
    if (!error && data) {
      setFighterProfile(data);
    } else if (error && error.code !== 'PGRST116') {
      // PGRST116 = not found, which is okay
      console.error('Error loading fighter profile:', error);
    }
  };

  /**
   * Load user badges
   */
  const loadUserBadges = async () => {
    const { data, error } = await rivalryApi.getUserBadges();
    if (!error && data) {
      setBadges(data);
    }
  };

  /**
   * Load user titles
   */
  const loadUserTitles = async () => {
    const { data, error } = await rivalryApi.getUserTitles();
    if (!error && data) {
      setTitles(data);
    }
  };

  /**
   * Load user credits
   */
  const loadUserCredits = async () => {
    const { data, error } = await rivalryApi.getUserCredits();
    if (!error && data) {
      setCredits(data);
    }
  };

  // ============================================================================
  // LOAD RIVALRY DATA
  // ============================================================================

  /**
   * Select and load a rivalry
   */
  const selectRivalry = async (rivalryId) => {
    setLoading(true);

    // Unsubscribe from previous subscriptions
    subscriptions.forEach(sub => sub.unsubscribe());
    setSubscriptions([]);

    // Load rivalry details
    const { data: rivalry, error: rivalryError } = await rivalryApi.getRivalryById(rivalryId);
    if (rivalryError) {
      console.error('Error loading rivalry:', rivalryError);
      setLoading(false);
      return;
    }

    setActiveRivalry(rivalry);

    // Load related data in parallel
    await Promise.all([
      loadLeaderboard(rivalryId),
      loadMessages(rivalryId),
      loadParticipants(rivalryId),
      loadUserStats(rivalryId),
      loadCurrentSeason(rivalryId),
    ]);

    // Set up real-time subscriptions
    setupSubscriptions(rivalryId);

    setLoading(false);
  };

  /**
   * Load leaderboard for current rivalry
   */
  const loadLeaderboard = async (rivalryId) => {
    const { data, error } = await rivalryApi.getRivalryLeaderboard(rivalryId);
    if (!error && data) {
      setLeaderboard(data);
    }
  };

  /**
   * Load messages for current rivalry
   */
  const loadMessages = async (rivalryId) => {
    const { data, error } = await rivalryApi.getRivalryMessages(rivalryId);
    if (!error && data) {
      setMessages(data);
    }
  };

  /**
   * Load participants for current rivalry
   */
  const loadParticipants = async (rivalryId) => {
    const { data, error } = await rivalryApi.getRivalryParticipants(rivalryId);
    if (!error && data) {
      setParticipants(data);
    }
  };

  /**
   * Load user stats for current rivalry
   */
  const loadUserStats = async (rivalryId) => {
    const { data, error } = await rivalryApi.getUserRivalryStats(rivalryId);
    if (!error && data) {
      setUserStats(data);
    }
  };

  /**
   * Load current season for rivalry
   */
  const loadCurrentSeason = async (rivalryId) => {
    const { data, error } = await rivalryApi.getCurrentSeason(rivalryId);
    if (!error && data) {
      setCurrentSeason(data);
    }
  };

  // ============================================================================
  // REAL-TIME SUBSCRIPTIONS
  // ============================================================================

  /**
   * Set up real-time subscriptions for a rivalry
   */
  const setupSubscriptions = (rivalryId) => {
    const leaderboardSub = rivalryApi.subscribeToLeaderboard(rivalryId, (payload) => {
      console.log('Leaderboard updated:', payload);
      loadLeaderboard(rivalryId);
    });

    const messagesSub = rivalryApi.subscribeToMessages(rivalryId, (payload) => {
      console.log('New message:', payload);
      setMessages((prev) => [...prev, payload.new]);
    });

    setSubscriptions([leaderboardSub, messagesSub]);
  };

  // ============================================================================
  // ACTIONS
  // ============================================================================

  /**
   * Create a new rivalry
   */
  const createRivalry = async (rivalryData) => {
    setLoading(true);
    const { data, error } = await rivalryApi.createRivalry(rivalryData);
    setLoading(false);

    if (!error && data) {
      await loadUserRivalries();
      return { success: true, rivalry: data };
    }

    return { success: false, error };
  };

  /**
   * Join a rivalry
   */
  const joinRivalry = async (rivalryId, nickname = null) => {
    setLoading(true);
    const { data, error } = await rivalryApi.joinRivalry(rivalryId, nickname);
    setLoading(false);

    if (!error && data) {
      await loadUserRivalries();
      return { success: true, data };
    }

    return { success: false, error };
  };

  /**
   * Leave a rivalry
   */
  const leaveRivalry = async (rivalryId) => {
    setLoading(true);
    const { error } = await rivalryApi.leaveRivalry(rivalryId);
    setLoading(false);

    if (!error) {
      await loadUserRivalries();
      if (activeRivalry?.id === rivalryId) {
        setActiveRivalry(null);
      }
      return { success: true };
    }

    return { success: false, error };
  };

  /**
   * Send a message
   */
  const sendMessage = async (content) => {
    if (!activeRivalry) return { success: false, error: 'No active rivalry' };

    const { data, error } = await rivalryApi.sendMessage(activeRivalry.id, content);

    if (!error && data) {
      return { success: true, message: data };
    }

    return { success: false, error };
  };

  /**
   * Update fighter profile
   */
  const updateFighterProfile = async (updates) => {
    setLoading(true);
    const { data, error } = await rivalryApi.updateFighterProfile(updates);
    setLoading(false);

    if (!error && data) {
      setFighterProfile(data);
      return { success: true, profile: data };
    }

    return { success: false, error };
  };

  /**
   * Record workout
   */
  const recordWorkout = async (workoutData) => {
    if (!activeRivalry) return { success: false, error: 'No active rivalry' };

    setLoading(true);
    const { data, error } = await rivalryApi.recordWorkoutPoints({
      ...workoutData,
      rivalryId: activeRivalry.id,
    });
    setLoading(false);

    if (!error && data) {
      // Refresh leaderboard and user stats
      await loadLeaderboard(activeRivalry.id);
      await loadUserStats(activeRivalry.id);
      return { success: true, workout: data };
    }

    return { success: false, error };
  };

  /**
   * Create invitation
   */
  const createInvitation = async (email = null) => {
    if (!activeRivalry) return { success: false, error: 'No active rivalry' };

    const { data, error } = await rivalryApi.createInvitation(activeRivalry.id, email);

    if (!error && data) {
      return { success: true, invitation: data };
    }

    return { success: false, error };
  };

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Load initial data on mount
  useEffect(() => {
    // Only load rivalry data if user is authenticated
    const initializeRivalryData = async () => {
      const { data: { session } } = await supabase.auth.getSession();

      if (session) {
        loadUserRivalries();
        loadFighterProfile();
        loadUserBadges();
        loadUserTitles();
        loadUserCredits();
      }
    };

    initializeRivalryData();

    // Cleanup subscriptions on unmount
    return () => {
      subscriptions.forEach(sub => sub.unsubscribe());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  const value = useMemo(
    () => ({
      // State
      rivalries,
      activeRivalry,
      loading,
      leaderboard,
      messages,
      participants,
      userStats,
      currentSeason,
      fighterProfile,
      badges,
      titles,
      credits,

      // Actions
      loadUserRivalries,
      selectRivalry,
      createRivalry,
      joinRivalry,
      leaveRivalry,
      sendMessage,
      updateFighterProfile,
      recordWorkout,
      createInvitation,
      loadFighterProfile,
      loadUserBadges,
      loadUserTitles,
      loadUserCredits,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      rivalries,
      activeRivalry,
      loading,
      leaderboard,
      messages,
      participants,
      userStats,
      currentSeason,
      fighterProfile,
      badges,
      titles,
      credits,
    ]
  );

  return <RivalryContext.Provider value={value}>{children}</RivalryContext.Provider>;
}

export function useRivalry() {
  const ctx = useContext(RivalryContext);
  if (!ctx) {
    throw new Error('useRivalry must be used within a RivalryProvider');
  }
  return ctx;
}
